import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, User, Phone, Mail, MapPin, Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";

const STATUSES = [
  { v: "new", l: "Pending review" },
  { v: "in_review", l: "In review" },
  { v: "accepted", l: "Accepted" },
  { v: "declined", l: "Declined" },
  { v: "closed", l: "Closed" },
];
const URGENCIES = [
  { v: "low", l: "Low" }, { v: "standard", l: "Standard" }, { v: "high", l: "High" }, { v: "crisis", l: "Crisis" },
];
const SERVICE_LABELS = {
  early_intervention: "Early Intervention", justice_support: "Justice Support",
  reintegration: "Reintegration", family: "Family & Carer", social_work: "Social Work",
  advocacy: "Advocacy", peer: "Peer Support",
};

export default function ReferralDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ref, setRef] = useState(null);
  const [staff, setStaff] = useState([]);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const load = () => api.get(`/referrals/${id}`).then((r) => setRef(r.data));

  useEffect(() => {
    load();
    api.get("/staff/list").then((r) => setStaff(r.data));
  }, [id]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/referrals/${id}`, body);
      setRef(data);
      toast.success("Referral updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const acceptAndCreateClient = async () => {
    if (!window.confirm("Accept this referral and create a client record?")) return;
    setAccepting(true);
    try {
      const { data } = await api.post(`/referrals/${id}/convert-to-client`);
      toast.success("Client created from referral");
      nav(`/dashboard/clients/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not accept"); }
    finally { setAccepting(false); }
  };

  const decline = async () => {
    if (!window.confirm("Decline this referral? You can add a note explaining why.")) return;
    await patch({ status: "declined" });
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/referrals/${id}/notes`, { body: note });
      setNote("");
      await load();
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingNote(false); }
  };

  if (!ref) return <div className="p-8 text-njas-muted">Loading…</div>;

  return (
    <div className="fade-in space-y-6" data-testid="referral-detail">
      <Link to="/dashboard/referrals" className="inline-flex items-center gap-1 text-sm text-njas-muted hover:text-njas-navy" data-testid="ref-back">
        <ArrowLeft className="w-4 h-4" /> Back to referrals
      </Link>

      <div className="rounded-xl bg-white border border-[var(--njas-border)] p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">{ref.reference_code}</div>
          <h1 className="mt-1 font-serif text-3xl text-njas-navy">
            {ref.client_first_name} {ref.client_last_name}
          </h1>
          <div className="mt-1 text-sm text-njas-muted capitalize">
            {ref.referral_type?.replace("_", " ")} referral · {new Date(ref.created_at).toLocaleString("en-AU")}
          </div>
          {ref.linked_client_id && (
            <Link to={`/dashboard/clients/${ref.linked_client_id}`} className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline">
              <CheckCircle2 className="w-3.5 h-3.5" /> Accepted · View client record <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {!ref.linked_client_id && (
            <>
              <button onClick={acceptAndCreateClient} disabled={accepting} data-testid="accept-referral-btn" className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-60">
                {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Accept & create client
              </button>
              <button onClick={decline} data-testid="decline-referral-btn" className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white text-red-600 px-4 py-2 text-sm hover:bg-red-50">
                <XCircle className="w-4 h-4" /> Decline
              </button>
            </>
          )}
          <select value={ref.status} onChange={(e) => patch({ status: e.target.value })} data-testid="ref-status-select" className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-2 px-3">
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <select value={ref.urgency || "standard"} onChange={(e) => patch({ urgency: e.target.value })} data-testid="ref-urgency-select" className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-2 px-3">
            {URGENCIES.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
          </select>
          <select value={ref.assigned_to || ""} onChange={(e) => patch({ assigned_to: e.target.value })} data-testid="ref-assign-select" className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-2 px-3">
            <option value="">Unassigned</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Panel title="Reason for referral">
            <p className="whitespace-pre-wrap text-njas-text leading-relaxed">{ref.reason}</p>
            {ref.additional_info && (
              <>
                <div className="njas-hairline my-4" />
                <div className="text-xs uppercase tracking-wider text-njas-muted mb-2">Additional info</div>
                <p className="whitespace-pre-wrap text-njas-text leading-relaxed">{ref.additional_info}</p>
              </>
            )}
          </Panel>

          <Panel title="Case notes">
            <form onSubmit={addNote} className="space-y-3" data-testid="add-note-form">
              <textarea
                value={note} onChange={(e) => setNote(e.target.value)}
                rows={3} placeholder="Add a case note…"
                data-testid="note-textarea"
                className="w-full rounded-lg border border-[var(--njas-border)] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage"
              />
              <div className="flex justify-end">
                <button type="submit" disabled={savingNote} data-testid="note-submit" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm font-medium hover:bg-njas-navy-hover disabled:opacity-60">
                  {savingNote && <Loader2 className="w-4 h-4 animate-spin" />} Add note
                </button>
              </div>
            </form>
            <div className="mt-6 space-y-4">
              {(ref.notes || []).length === 0 && <div className="text-sm text-njas-muted">No notes yet.</div>}
              {(ref.notes || []).slice().reverse().map((n) => (
                <div key={n.id} className="rounded-lg border border-[var(--njas-border)] p-4" data-testid={`note-${n.id}`}>
                  <div className="text-xs text-njas-muted mb-1">
                    {n.author_name} · {new Date(n.created_at).toLocaleString("en-AU")}
                  </div>
                  <div className="text-sm text-njas-text whitespace-pre-wrap">{n.body}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Client details">
            <ContactRow icon={User} label={`${ref.client_first_name} ${ref.client_last_name}${ref.client_age ? ` · ${ref.client_age}` : ""}`} />
            {ref.client_phone && <ContactRow icon={Phone} label={ref.client_phone} />}
            {ref.client_email && <ContactRow icon={Mail} label={ref.client_email} />}
            {ref.client_location && <ContactRow icon={MapPin} label={ref.client_location} />}
          </Panel>

          {ref.referral_type === "third_party" && (
            <Panel title="Referrer">
              {ref.referrer_name && <ContactRow icon={User} label={`${ref.referrer_name}${ref.referrer_relationship ? ` · ${ref.referrer_relationship}` : ""}`} />}
              {ref.referrer_phone && <ContactRow icon={Phone} label={ref.referrer_phone} />}
              {ref.referrer_email && <ContactRow icon={Mail} label={ref.referrer_email} />}
              {ref.referrer_organisation && <div className="text-sm text-njas-muted">Org: {ref.referrer_organisation}</div>}
            </Panel>
          )}

          <Panel title="Services requested">
            <div className="flex flex-wrap gap-2">
              {(ref.service_types || []).map((s) => (
                <span key={s} className="px-3 py-1 rounded-full bg-njas-sage-tint text-njas-sage-hover text-xs font-medium">
                  {SERVICE_LABELS[s] || s}
                </span>
              ))}
              {(ref.service_types || []).length === 0 && <span className="text-sm text-njas-muted">—</span>}
            </div>
            <div className="mt-4 text-xs text-njas-muted">Justice involvement: <span className="text-njas-text capitalize">{(ref.justice_involvement || "—").replace("_", " ")}</span></div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="rounded-xl bg-white border border-[var(--njas-border)] p-6">
      <div className="eyebrow text-njas-sage-hover text-[10px] mb-3">{title}</div>
      {children}
    </section>
  );
}
function ContactRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 text-sm text-njas-text py-1.5">
      <Icon className="w-4 h-4 text-njas-muted" /> {label}
    </div>
  );
}
